import { Typography } from '@bowtie-ins/bowkit';
import { Box, Slider } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TERM_LIFE_OPTIONS, TermLifeYear } from '../constants/termLife';
import MarketingPageAnchorLink from './MarketingPageAnchorLink';

const EnhancedSlider = withStyles(() => ({
  thumb: {
    width: 16,
    height: 16,
    marginTop: -6,
    border: '2px solid currentColor',
  },
  track: {
    height: 4,
    borderRadius: 4,
    color: '#dcdcdc',
  },
  mark: {
    width: 6,
    height: 6,
    marginTop: -3,
    color: '#dcdcdc',
    border: '2px solid currentColor',
    borderRadius: '50%',
  },
  markActive: { backgroundColor: 'currentColor' },
}))(Slider);

type Props = {
  termLifeYear: TermLifeYear;
  onTermLifeYearChange: (year: TermLifeYear) => void;
  showAnchorLink: boolean;
};

const TermLifeSlider = ({
  termLifeYear = '1',
  onTermLifeYearChange,
  showAnchorLink,
}: Props) => {
  const intl = useIntl();
  const committedIndex = TERM_LIFE_OPTIONS.findIndex(
    o => o.year === termLifeYear,
  );
  const [displayIndex, setDisplayIndex] = useState(committedIndex);
  const currentOption = TERM_LIFE_OPTIONS[displayIndex];

  // Re-sync local drag state when the committed year changes from outside
  // (browser back/forward, programmatic QS update, hydration).
  useEffect(() => {
    setDisplayIndex(committedIndex);
  }, [committedIndex]);

  return (
    <>
      <Typography variant="body-l">
        <FormattedMessage id="termLifeYear.label" />
      </Typography>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="title-m" fontWeight="bold">
          <FormattedMessage id={`termLifeYear.${currentOption.year}`} />
        </Typography>
        {showAnchorLink && (
          <MarketingPageAnchorLink
            variant="term-level-life-premium-explanation"
            productType="life"
          />
        )}
      </Box>
      <EnhancedSlider
        data-cy="term-life-year-slider"
        aria-labelledby="term-life-year-label"
        aria-valuetext={intl.formatMessage({
          id: `termLifeYear.${currentOption.year}`,
        })}
        step={1}
        min={0}
        max={TERM_LIFE_OPTIONS.length - 1}
        marks
        value={displayIndex}
        onChange={(_event, newValue) => {
          if (typeof newValue === 'number') {
            setDisplayIndex(newValue);
          }
        }}
        onChangeCommitted={(_event, newValue) => {
          if (typeof newValue === 'number') {
            onTermLifeYearChange(TERM_LIFE_OPTIONS[newValue].year);
          }
        }}
        data-ui-component="Slider"
        data-ui-variant="Term Life Year"
      />
    </>
  );
};

export default TermLifeSlider;
